import React, { useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  TableCell,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

/**
 * Pieces the tag manager and the list manager share, so the two screens look
 * and behave alike.
 */

/* ------------------------------------------------------------------ *
 * A number at the top of a screen, which filters the table below it
 * ------------------------------------------------------------------ */

interface StatTileProps {
  label: string;
  value: number | undefined;
  hint: string;
  icon: React.ReactNode;
  /** The tile's accent: a palette colour such as '#2e7d32'. */
  tone: string;
  selected: boolean;
  onClick: () => void;
}

export const StatTile = ({ label, value, hint, icon, tone, selected, onClick }: StatTileProps) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={selected}
    sx={(theme) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 1.5,
      p: 1.75,
      textAlign: 'left',
      borderRadius: 2,
      border: '1px solid',
      borderColor: selected ? alpha(tone, 0.55) : theme.palette.divider,
      bgcolor: selected ? alpha(tone, 0.06) : theme.palette.background.paper,
      boxShadow: selected ? `0 0 0 1px ${alpha(tone, 0.35)}` : 'none',
      transition: 'border-color 120ms, background-color 120ms, box-shadow 120ms',
      '&:hover': { borderColor: alpha(tone, 0.45) },
      '&.Mui-focusVisible': { outline: '2px solid', outlineColor: tone, outlineOffset: 2 },
    })}
  >
    <Box
      aria-hidden
      sx={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: 1.5,
        display: 'grid',
        placeItems: 'center',
        color: tone,
        bgcolor: alpha(tone, 0.1),
        '& svg': { fontSize: 22 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="span"
        sx={{ display: 'block', fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}
      >
        {value === undefined ? <Skeleton width={36} /> : value.toLocaleString('en-US')}
      </Typography>
      <Typography component="span" sx={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
        {hint}
      </Typography>
    </Box>
  </ButtonBase>
);

export const StatTiles = ({ label, columns = 4, children }: { label: string; columns?: number; children: React.ReactNode }) => (
  <Box
    role="group"
    aria-label={label}
    sx={{
      display: 'grid',
      gap: 1.5,
      gridTemplateColumns: { xs: columns === 4 ? '1fr 1fr' : '1fr', sm: '1fr 1fr', md: `repeat(${columns}, minmax(0, 1fr))` },
    }}
  >
    {children}
  </Box>
);

/* ------------------------------------------------------------------ *
 * Nothing to show
 * ------------------------------------------------------------------ */

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  text: string;
  action?: React.ReactNode;
}

export const EmptyState = ({ icon, title, text, action }: EmptyStateProps) => (
  <Box sx={{ py: 7, px: 2, display: 'grid', justifyItems: 'center', gap: 1, textAlign: 'center' }}>
    <Box
      aria-hidden
      sx={(theme) => ({
        width: 56,
        height: 56,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        color: 'primary.main',
        mb: 1,
      })}
    >
      {icon}
    </Box>
    <Typography variant="h6" component="p">
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
      {text}
    </Typography>
    {action && <Box sx={{ mt: 1 }}>{action}</Box>}
  </Box>
);

/** Grey rows while the first answer is on its way. */
export const LoadingRows = ({ columns, rows = 6 }: { columns: number; rows?: number }) => (
  <>
    {Array.from({ length: rows }, (_, row) => (
      <TableRow key={row} aria-hidden>
        {Array.from({ length: columns }, (__, column) => (
          <TableCell key={column}>
            <Skeleton width={column === 0 ? '70%' : '50%'} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

/** Read by screen readers, not shown: a column heading with nothing to show, such as the actions. */
export const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
} as const;

/** Table styling both managers use: quiet headers, one line a row, a soft hover. */
export const TABLE_SX = {
  '& .MuiTableCell-root': { borderColor: '#eef1f5' },
  '& .MuiTableCell-head': {
    color: 'text.secondary',
    fontSize: '0.75rem',
    letterSpacing: '0.02em',
    py: 1.25,
    backgroundColor: '#f8fafc',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableCell-body': { py: 1.25, fontSize: '0.8125rem' },
  '& tbody tr:last-of-type td': { borderBottom: 0 },
  '& .MuiTableRow-hover:hover': { backgroundColor: '#f8fafc' },
} as const;

/* ------------------------------------------------------------------ *
 * Deleting, confirmed
 * ------------------------------------------------------------------ */

interface DeleteDialogProps {
  title: string;
  /** What deleting does, read before the button is pressed. */
  children: React.ReactNode;
  /** The red button. */
  confirm: string;
  /**
   * The name to type before the button works, for a delete that reaches people.
   * Matched ignoring case and surrounding spaces.
   */
  typeToConfirm?: string;
  /** A refusal to show, already in words. */
  error?: string;
  running: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const DeleteDialog = ({ title, children, confirm, typeToConfirm, error, running, onConfirm, onClose }: DeleteDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [typed, setTyped] = useState('');
  const matches = !typeToConfirm || typed.trim().toLowerCase() === typeToConfirm.trim().toLowerCase();

  return (
    <Dialog
      open
      onClose={running ? undefined : onClose}
      fullScreen={phone}
      fullWidth
      maxWidth="sm"
      aria-labelledby="crm-delete-title"
    >
      <DialogTitle id="crm-delete-title">{title}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'grid', gap: 1, color: 'text.secondary', fontSize: '0.875rem' }}>{children}</Box>
        {typeToConfirm && (
          <TextField
            label={`Type "${typeToConfirm}" to confirm`}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            disabled={running}
            autoComplete="off"
            size="small"
            fullWidth
          />
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={running}>
          Keep it
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={onConfirm}
          disabled={running || !matches}
          startIcon={running ? <CircularProgress size={16} color="inherit" aria-label="Working" /> : undefined}
        >
          {confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
